import { useState } from 'react';
import { useLoad } from '../hooks/useLoad';
import { useService } from '../hooks/useService';
import { RangeCard } from './RangeCard';

/**
 * 사례보고서 축어록 구간 고르기 (A32): 모든 회기에서 핵심 포인트가 많은 구간을 점수 순으로 보여주고, 하나를 고른다.
 * 고르면 사례보고서의 축어록 칸(이유·시간·본문)이 그 구간으로 바뀐다. 포인트 표시는 문서에 들어가지 않는다.
 */
export function VerbatimPicker({ clientId, docId, onChanged }: { clientId: string; docId?: string; onChanged: () => void }) {
  const svc = useService();
  const { data, reload } = useLoad(() => svc.verbatimPickCandidates(clientId), [svc, clientId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!data) return <p className="muted small">구간 찾는 중…</p>;
  if (!data.length) return <p className="muted small">고를 구간이 없습니다 (처리된 회기가 없음).</p>;
  return (
    <div className="stack">
      <p className="muted small">
        치료를 진행한 흔적(반영, 구체화 질문, 기법)과 내담자의 알아차림·변화가 많이 드러나는 구간이 위에 옵니다. 하나만 고르면 사례보고서
        축어록으로 들어갑니다. 줄마다 붙은 포인트 표시는 고를 때만 보이고 문서에는 들어가지 않습니다.
      </p>
      {data.map((c) => (
        <RangeCard
          key={c.key}
          title={`${c.sessionNumber}회기`}
          badges={
            <>
              {c.source === 'ai' && <span className="badge badge-auto">AI 변화 지점</span>}
              {c.chosen && <span className="badge badge-user">사례보고서에 쓰는 구간</span>}
            </>
          }
          startMs={c.startMs}
          endMs={c.endMs}
          sessionEndMs={c.sessionEndMs}
          points={c.points}
          reason={c.reason}
          lines={c.lines}
          chosen={c.chosen}
          actions={
            c.chosen ? (
              <span className="ok small">✓ 이 구간을 쓰고 있습니다</span>
            ) : (
              <button
                type="button"
                className="primary"
                disabled={!!busy}
                onClick={() => {
                  setBusy(c.key);
                  setErr(null);
                  void svc
                    .setCaseReportVerbatim(c.sessionId, { startMs: c.startMs, endMs: c.endMs, reason: c.reason })
                    .then(() => (docId ? svc.refreshReportVerbatim(docId) : undefined))
                    .then(() => {
                      reload();
                      onChanged();
                    })
                    .catch((e: Error) => setErr(e.message))
                    .finally(() => setBusy(null));
                }}
              >
                {busy === c.key ? '바꾸는 중…' : '이 구간을 사례보고서 축어록으로'}
              </button>
            )
          }
        />
      ))}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
    </div>
  );
}
